import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';

/**
 * Protected Route Component for Platform Admin Pages
 * 
 * Ensures only authenticated platform admins can access protected routes.
 * Redirects to login if not authenticated.
 */

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireSuperAdmin = false 
}) => {
  const { isAuthenticated, isLoading, isAuthInitialized, platformAdmin } = usePlatformAdminAuth();

  // Wait for auth initialization before making any redirect decisions
  if (!isAuthInitialized || isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/platform-admin/login" replace />;
  }

  if (requireSuperAdmin && !platformAdmin?.is_super_admin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-6 py-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p>Super admin privileges required to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
